import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type MarkedTenant,
  type RemovalLogEntry,
  type RemovalLogKind,
  type RemovalTrigger,
  type SweepCandidate,
  type SweepPreview,
  type SweepRule,
  type SweepRunRow,
  type SweepRunTrigger,
  type SweepSkipReason,
  type SweepStatus,
  type SweeperConfig,
  type SweeperConfigResponse,
  type Tier,
  type WebhookFailure,
} from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge, statusTone } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input, Label, Select } from '@/components/ui/input';
import { PostponeDialog } from '@/components/PostponeDialog';
import { Check, ClipboardCopy, Download, FileText, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { formatDate, relativeTime, removalDueLabel } from '@/lib/utils';
import { downloadCsv, toCsv } from '@/lib/csv';
// Spec for whoever builds the receiving endpoint, shown in a dialog and
// copyable as markdown. Kept as a real .md file so it stays reviewable.
import webhookReceiverDocs from '@/content/webhook-receiver.md?raw';

const SKIP_REASON: Record<SweepSkipReason, string> = {
  activity_never_read: 'Activity never read',
  activity_stale: 'No recent activity reading (tenant not reachable lately)',
  ai_auth_unknown: 'No recent reading of whether an AI is connected',
  site_count_unknown: 'Site count unknown',
  protected: 'Marked or postponed',
  never_delete: 'Never delete is on for this account',
  check_failed: 'Live check failed (tenant not reachable)',
};

const TRIGGER: Record<RemovalTrigger, string> = {
  sweeper_auto: 'Sweeper (auto)',
  sweeper_manual: 'Sweeper (manual)',
  operator: 'Operator',
  external_api: 'External API',
};

const RUN_TRIGGER: Record<SweepRunTrigger, string> = {
  auto: 'Scheduled',
  run_now: 'Run now',
  manual: 'Manual',
};

// Why a mark was cleared (removal_log detail.reason / webhook removal.reason).
const UNMARK_REASON: Record<string, string> = {
  operator: 'unmarked by an operator',
  active_again: 'used again',
  site_registered: 'registered a website',
  ai_authenticated: 'connected an AI provider',
  tier_changed: 'tier changed',
  status_changed: 'status changed',
  rule_changed: 'rule changed',
  rule_removed: 'rule removed',
};

const CHECKED: Record<SweepCandidate['checked'], string> = {
  live: 'live',
  db_only: 'stored data',
  not_needed: '—',
};

const KIND_TONE: Record<RemovalLogKind, 'danger' | 'warning' | 'success' | 'info'> = {
  removed: 'danger',
  marked: 'warning',
  unmarked: 'success',
  postponed: 'info',
};

const WEBHOOK_TONE: Record<string, 'success' | 'danger' | 'warning' | 'neutral'> = {
  delivered: 'success',
  failed: 'danger',
  pending: 'warning',
  not_configured: 'neutral',
  skipped: 'neutral',
};

// <a> styled like an outline sm Button — downloads must be real links so the
// session cookie rides along and the browser saves the file.
const LINK_BUTTON =
  'inline-flex items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 h-8 px-3 text-xs border border-slate-300 bg-white text-slate-900 hover:bg-slate-50';

const TABLE_HEAD = 'text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200';
const ROW = 'border-b border-slate-100 last:border-0 align-top';

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

function toInt(v: string): number {
  const n = Math.floor(Number(v));
  return Number.isFinite(n) ? n : 0;
}

function tierLabel(tiers: Tier[], id: string | null): string {
  if (!id) return '—';
  return tiers.find((t) => t.id === id)?.label ?? id;
}

function newRule(name: string): SweepRule {
  return {
    name, last_used_days: 90, registered_before_days: null, tiers: [],
    no_sites: false, one_site: false, no_ai_auth: false,
  };
}

function describeRule(rule: Omit<SweepRule, 'name'>, tiers: Tier[]): string {
  const parts = [`not used for ${rule.last_used_days}+ days`];
  if (rule.registered_before_days) parts.push(`registered ${rule.registered_before_days}+ days ago`);
  if (rule.tiers.length) parts.push(`tier ${rule.tiers.map((t) => tierLabel(tiers, t)).join(' or ')}`);
  if (rule.no_sites) parts.push('no websites');
  if (rule.one_site) parts.push('exactly 1 website');
  if (rule.no_ai_auth) parts.push('no AI authenticated');
  return parts.join(', ');
}

type RunCounts = {
  candidates?: number;
  marked?: number;
  removed?: number;
  unmarked?: number;
  failed?: number;
  deferred?: number;
  no_longer_matching?: number;
  unknown_skipped?: number;
};

function runSummaryText(p: RunCounts, dryRun = false): string {
  const parts: string[] = [];
  if (dryRun) parts.push(`${p.candidates ?? 0} ${p.candidates === 1 ? 'match' : 'matches'}`);
  if (p.marked) parts.push(`${p.marked} marked`);
  if (p.removed) parts.push(`${p.removed} removed`);
  if (p.unmarked) parts.push(`${p.unmarked} unmarked`);
  if (p.failed) parts.push(`${p.failed} failed`);
  if (p.deferred) parts.push(`${p.deferred} left for the next run`);
  if (p.no_longer_matching) parts.push(`${p.no_longer_matching} no longer matching`);
  if (p.unknown_skipped) parts.push(`${p.unknown_skipped} skipped (unknown)`);
  return parts.length ? parts.join(', ') : 'nothing to do';
}

function ErrorBox({ children }: { children: ReactNode }) {
  return <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{children}</div>;
}

function NoteBox({ children }: { children: ReactNode }) {
  return <div className="text-xs text-amber-800 bg-amber-50 rounded-md px-3 py-2">{children}</div>;
}

type LogFilters = {
  kind: RemovalLogKind | 'all';
  trigger: RemovalTrigger | '';
  from: string;
  to: string;
  tenant: string;
  run_id: string;
};

const DEFAULT_LOG_FILTERS: LogFilters = { kind: 'removed', trigger: '', from: '', to: '', tenant: '', run_id: '' };

function logQuery(f: LogFilters): string {
  return new URLSearchParams(Object.entries(f).filter(([, v]) => v !== '')).toString();
}

export function CleanupPage() {
  const qc = useQueryClient();
  const config = useQuery({
    queryKey: ['sweeper-config'],
    queryFn: () => api.get<SweeperConfigResponse>('/api/sweeper/config'),
  });
  const tiers = useQuery({
    queryKey: ['tiers'],
    queryFn: () => api.get<{ tiers: Tier[] }>('/api/tiers'),
  });
  const status = useQuery({
    queryKey: ['sweeper-status'],
    queryFn: () => api.get<SweepStatus>('/api/sweeper/status'),
    refetchInterval: (q) => (q.state.data?.running ? 2_000 : 15_000),
  });
  const [logFilters, setLogFilters] = useState<LogFilters>(DEFAULT_LOG_FILTERS);
  const logRef = useRef<HTMLDivElement>(null);

  // A run just finished: refresh everything it may have touched.
  const runningId = status.data?.running?.run_id ?? null;
  const prevRunning = useRef<string | null>(null);
  useEffect(() => {
    if (prevRunning.current && !runningId) {
      for (const key of ['sweeper-runs', 'sweeper-log', 'sweeper-marked', 'sweeper-config', 'tenants']) {
        qc.invalidateQueries({ queryKey: [key] });
      }
    }
    prevRunning.current = runningId;
  }, [runningId, qc]);

  const viewRunLog = (runId: string) => {
    setLogFilters({ ...DEFAULT_LOG_FILTERS, kind: 'all', run_id: runId });
    logRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const header = (
    <div>
      <h1 className="text-2xl font-semibold text-slate-900">Cleanup</h1>
      <p className="text-sm text-slate-500 mt-1">
        Find and remove unused accounts. A removal always stops the container and purges all of its
        data.
      </p>
    </div>
  );

  if (config.isLoading || tiers.isLoading) {
    return (
      <div className="space-y-6">
        {header}
        <div className="text-sm text-slate-500">Loading…</div>
      </div>
    );
  }
  if (config.isError || !config.data) {
    return (
      <div className="space-y-6">
        {header}
        <ErrorBox>Could not load the sweeper settings: {(config.error as Error | null)?.message ?? 'unknown error'}</ErrorBox>
      </div>
    );
  }

  const tierList = tiers.data?.tiers ?? [];
  const running = !!status.data?.running;

  return (
    <div className="space-y-6">
      {header}
      <RunStatusBanner status={status.data} onViewLog={viewRunLog} />
      <ManualSweepCard tiers={tierList} data={config.data} running={running} />
      <AutoSweeperCard tiers={tierList} data={config.data} running={running} />
      <WebhooksCard data={config.data} />
      <MarkedCard tiers={tierList} graceDays={config.data.config.grace_days} />
      <RecentSweepsCard tiers={tierList} onViewLog={viewRunLog} />
      <div ref={logRef} className="scroll-mt-6">
        <RemovalLogCard tiers={tierList} filters={logFilters} onFilters={setLogFilters} />
      </div>
    </div>
  );
}

const RUN_KIND_LABEL = { manual_remove: 'Manual removal', manual_mark: 'Manual marking', auto: 'Automatic sweep' } as const;
const PHASE_LABEL = { checking: 'checking accounts', marking: 'marking', removing: 'removing' } as const;

function RunStatusBanner({ status, onViewLog }: { status: SweepStatus | undefined; onViewLog: (runId: string) => void }) {
  const [dismissed, setDismissed] = useState<string | null>(null);
  if (!status) return null;
  if (status.running) {
    const r = status.running;
    return (
      <div className="rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900 flex items-center gap-2">
        <RefreshCw size={14} className="animate-spin shrink-0" />
        <span>
          {RUN_KIND_LABEL[r.kind]} running — {PHASE_LABEL[r.phase]}
          {r.total ? ` ${r.done}/${r.total}` : ''}…
        </span>
      </div>
    );
  }
  const last = status.last;
  if (!last || dismissed === last.run_id) return null;
  const bad = !!last.error || last.failed > 0;
  return (
    <div
      className={`rounded-md border px-4 py-3 text-sm flex flex-wrap items-center gap-x-3 gap-y-1 ${
        bad ? 'border-red-200 bg-red-50 text-red-900' : 'border-slate-200 bg-slate-50 text-slate-800'
      }`}
    >
      <span className="min-w-0">
        {RUN_KIND_LABEL[last.kind]} finished {relativeTime(last.finished_at)}:{' '}
        {last.error ? `crashed — ${last.error}` : runSummaryText(last)}.
      </span>
      <button type="button" className="text-xs underline" onClick={() => onViewLog(last.run_id)}>
        View log
      </button>
      <button
        type="button"
        className="ml-auto text-slate-400 hover:text-slate-700"
        onClick={() => setDismissed(last.run_id)}
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}

function RuleFields({
  rule,
  onChange,
  tiers,
  showName,
}: {
  rule: SweepRule;
  onChange: (r: SweepRule) => void;
  tiers: Tier[];
  showName: boolean;
}) {
  return (
    <div className="space-y-3">
      {showName && (
        <div className="max-w-sm">
          <Label>Rule name</Label>
          <Input value={rule.name} onChange={(e) => onChange({ ...rule, name: e.target.value })} />
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
        <div>
          <Label>Not used for more than (days)</Label>
          <Input
            type="number"
            min={1}
            value={rule.last_used_days}
            onChange={(e) => onChange({ ...rule, last_used_days: toInt(e.target.value) })}
          />
          <p className="text-xs text-slate-500 mt-1">Last chat activity. Never used = since registration.</p>
        </div>
        <div>
          <Label>Registered more than (days ago) — optional</Label>
          <Input
            type="number"
            min={1}
            placeholder="Any"
            value={rule.registered_before_days ?? ''}
            onChange={(e) =>
              onChange({
                ...rule,
                registered_before_days: e.target.value === '' ? null : toInt(e.target.value),
              })
            }
          />
        </div>
      </div>
      <div>
        <Label>Tiers</Label>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {tiers.map((t) => (
            <label key={t.id} className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer select-none">
              <input
                type="checkbox"
                className="cursor-pointer"
                checked={rule.tiers.includes(t.id)}
                onChange={(e) =>
                  onChange({
                    ...rule,
                    tiers: e.target.checked ? [...rule.tiers, t.id] : rule.tiers.filter((x) => x !== t.id),
                  })
                }
              />
              {t.label}
            </label>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-1">None checked = every tier.</p>
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer select-none">
          <input
            type="checkbox"
            className="cursor-pointer"
            checked={rule.no_sites}
            onChange={(e) => onChange({ ...rule, no_sites: e.target.checked, one_site: false })}
          />
          No websites registered
        </label>
        <label className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer select-none">
          <input
            type="checkbox"
            className="cursor-pointer"
            checked={rule.one_site}
            onChange={(e) => onChange({ ...rule, one_site: e.target.checked, no_sites: false })}
          />
          Exactly 1 website registered
        </label>
        <label className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer select-none">
          <input
            type="checkbox"
            className="cursor-pointer"
            checked={rule.no_ai_auth}
            onChange={(e) => onChange({ ...rule, no_ai_auth: e.target.checked })}
          />
          No AI authenticated (no connected subscription, no API key)
        </label>
      </div>
    </div>
  );
}

function downloadPreviewCsv(result: SweepPreview, filename: string) {
  const header = [
    'result', 'tenant_id', 'customer_email', 'customer_name', 'company_name', 'tier', 'status',
    'registered_at', 'last_used_at', 'idle_days', 'site_count', 'ai_authenticated', 'rule',
    'checked', 'marked_at', 'due_at', 'server_id',
  ];
  const row = (c: SweepCandidate, result: string) => [
    result, c.tenant_id, c.customer_email, c.customer_name, c.company_name, c.tier, c.status,
    c.created_at, c.last_used_at, c.idle_days, c.site_count, c.ai_authenticated, c.rule,
    c.checked, c.marked_at, c.due_at, c.server_id,
  ];
  downloadCsv(
    filename,
    toCsv(header, [
      ...result.candidates.map((c) => row(c, 'candidate')),
      ...result.skipped.map((c) => row(c, `skipped: ${c.reason ?? ''}`)),
    ])
  );
}

function PreviewResults({
  result,
  tiers,
  filename,
  selection,
}: {
  result: SweepPreview;
  tiers: Tier[];
  filename: string;
  selection?: TableSelection;
}) {
  const { candidates, skipped } = result;
  const siteUnknown = skipped.some((s) => s.reason === 'site_count_unknown');
  const authUnknown = skipped.some((s) => s.reason === 'ai_auth_unknown');
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-slate-700">
          <b>{plural(candidates.length, 'account')}</b> match
          {skipped.length > 0 && <>, {skipped.length} skipped</>}.
          {selection && (
            <>
              {' '}
              Untick any you want left alone —{' '}
              <b>{candidates.filter((c) => selection.selected.has(c.tenant_id)).length}</b> of{' '}
              {candidates.length} selected.
            </>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={() => downloadPreviewCsv(result, filename)}>
          <Download size={12} /> Download CSV
        </Button>
      </div>
      {candidates.length > 0 && <CandidateTable rows={candidates} tiers={tiers} selection={selection} />}
      {siteUnknown && (
        <NoteBox>
          Some accounts have an unknown site count, so "No websites registered" can't match them yet.
          The 5-minute host sweep fills it in; it stays unknown only for a tenant with no data
          directory on its server.
        </NoteBox>
      )}
      {authUnknown && (
        <NoteBox>
          For some accounts we couldn't read, recently enough, whether an AI is connected, so "No AI
          connected" won't match them — an account is never removed for having no AI on a reading we
          can't vouch for. This check runs over SSH at the start of every sweep; it stays unread only
          for a server the control plane can't reach. Check that server's health.
        </NoteBox>
      )}
      {skipped.length > 0 && (
        <details className="border border-slate-200 rounded-md">
          <summary className="px-3 py-2 text-xs text-slate-700 cursor-pointer">
            Skipped ({skipped.length}) — would match, but can't be confirmed or are already in the grace period
          </summary>
          <div className="overflow-x-auto border-t border-slate-200">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className={TABLE_HEAD}>
                  <th className="px-3 py-2">Tenant</th>
                  <th className="px-3 py-2">Why skipped</th>
                  <th className="px-3 py-2">Tier</th>
                  <th className="px-3 py-2">Last used</th>
                  <th className="px-3 py-2">Rule</th>
                </tr>
              </thead>
              <tbody>
                {skipped.map((s) => (
                  <tr key={s.tenant_id} className={ROW}>
                    <td className="px-3 py-2 font-mono text-xs">
                      <Link to={`/tenants/${s.tenant_id}`} className="text-slate-900 hover:underline">
                        {s.tenant_id}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-700">
                      {s.reason ? SKIP_REASON[s.reason] : '—'}
                      {s.reason === 'protected' && s.due_at && ` (removal ${removalDueLabel(s.due_at)})`}
                    </td>
                    <td className="px-3 py-2 text-xs">{tierLabel(tiers, s.tier)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500" title={formatDate(s.last_used_at)}>
                      {s.idle_days}d ago
                    </td>
                    <td className="px-3 py-2 text-xs">{s.rule ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}

// `selection` is optional: the automatic sweeper's preview is read-only, the
// manual sweep's is a pick list. Selection lives in the parent, since it is
// what the action buttons act on.
export interface TableSelection {
  selected: Set<string>;
  onToggle: (tenantId: string) => void;
  onToggleAll: (checked: boolean) => void;
}

function CandidateTable({
  rows,
  tiers,
  selection,
}: {
  rows: SweepCandidate[];
  tiers: Tier[];
  selection?: TableSelection;
}) {
  const allSelected = !!selection && rows.length > 0 && rows.every((r) => selection.selected.has(r.tenant_id));
  const someSelected = !!selection && rows.some((r) => selection.selected.has(r.tenant_id));
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-md max-h-[480px] overflow-y-auto">
      <table className="w-full min-w-[980px] text-sm">
        <thead className="sticky top-0 bg-white">
          <tr className={TABLE_HEAD}>
            {selection && (
              <th className="px-3 py-2 w-8">
                <input
                  type="checkbox"
                  className="cursor-pointer"
                  aria-label="Select every account"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={(e) => selection.onToggleAll(e.target.checked)}
                />
              </th>
            )}
            <th className="px-3 py-2">Tenant</th>
            <th className="px-3 py-2">Customer</th>
            <th className="px-3 py-2">Tier</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Last used</th>
            <th className="px-3 py-2">Registered</th>
            <th className="px-3 py-2">Sites</th>
            <th className="px-3 py-2">AI auth</th>
            <th className="px-3 py-2">Rule</th>
            <th className="px-3 py-2" title="Whether usage was re-read from the tenant just now, or judged on stored data">Checked</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.tenant_id} className={ROW}>
              {selection && (
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    className="cursor-pointer"
                    aria-label={`Include ${c.tenant_id}`}
                    checked={selection.selected.has(c.tenant_id)}
                    onChange={() => selection.onToggle(c.tenant_id)}
                  />
                </td>
              )}
              <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                <Link to={`/tenants/${c.tenant_id}`} className="text-slate-900 hover:underline">
                  {c.tenant_id}
                </Link>
                {c.marked_at && (
                  <Badge tone="danger" className="ml-2">
                    marked
                  </Badge>
                )}
              </td>
              <td className="px-3 py-2 text-xs">
                <div className="text-slate-800">{c.customer_email ?? '—'}</div>
                {c.company_name && <div className="text-slate-500">{c.company_name}</div>}
              </td>
              <td className="px-3 py-2 text-xs">{tierLabel(tiers, c.tier)}</td>
              <td className="px-3 py-2">
                <Badge tone={statusTone(c.status)}>{c.status}</Badge>
              </td>
              <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap" title={formatDate(c.last_used_at)}>
                {c.idle_days}d ago
              </td>
              <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap" title={formatDate(c.created_at)}>
                {relativeTime(c.created_at)}
              </td>
              <td className="px-3 py-2 text-xs">{c.site_count ?? '?'}</td>
              <td className="px-3 py-2 text-xs">{c.ai_authenticated ? 'yes' : 'no'}</td>
              <td className="px-3 py-2 text-xs">{c.rule ?? '—'}</td>
              <td className="px-3 py-2 text-xs text-slate-500">{CHECKED[c.checked]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ManualSweepCard({ tiers, data, running }: { tiers: Tier[]; data: SweeperConfigResponse; running: boolean }) {
  const qc = useQueryClient();
  const [rule, setRule] = useState<SweepRule>(() => newRule('Manual sweep'));
  const [preview, setPreview] = useState<{ rule: SweepRule; result: SweepPreview } | null>(null);
  const [confirm, setConfirm] = useState<'remove' | 'mark' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Which of the previewed accounts this sweep will actually touch. Everything
  // is ticked when a dry run lands; unticking is how an operator spares one
  // account for this run only ("Never delete" on the tenant page is the
  // permanent version, and those never reach this list).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const candidates = preview?.result.candidates ?? [];
  const markable = candidates.filter((c) => !c.marked_at);
  const pickedCandidates = candidates.filter((c) => selected.has(c.tenant_id));
  const pickedMarkable = markable.filter((c) => selected.has(c.tenant_id));
  const selection: TableSelection = {
    selected,
    onToggle: (id) =>
      setSelected((prev) => {
        const next = new Set(prev);
        if (!next.delete(id)) next.add(id);
        return next;
      }),
    onToggleAll: (checked) =>
      setSelected(checked ? new Set(candidates.map((c) => c.tenant_id)) : new Set()),
  };
  // Acting always uses the previewed criteria + the previewed accounts; any
  // edit after the dry run means the operator hasn't seen the result yet.
  const stale = !!preview && JSON.stringify(preview.rule) !== JSON.stringify(rule);
  const grace = data.config.grace_days;

  const dryRun = useMutation({
    mutationFn: () => api.post<SweepPreview>('/api/sweeper/run', { dry_run: true, rules: [rule] }),
    onSuccess: (result) => {
      setPreview({ rule, result });
      setSelected(new Set(result.candidates.map((c) => c.tenant_id)));
      setError(null);
      qc.invalidateQueries({ queryKey: ['sweeper-runs'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const execute = useMutation({
    mutationFn: (action: 'remove' | 'mark') =>
      api.post<{ run_id: string }>('/api/sweeper/run', {
        dry_run: false,
        action,
        rules: [preview!.rule],
        tenant_ids: (action === 'remove' ? pickedCandidates : pickedMarkable).map((c) => c.tenant_id),
      }),
    onSuccess: () => {
      setConfirm(null);
      setPreview(null);
      setSelected(new Set());
      setError(null);
      qc.invalidateQueries({ queryKey: ['sweeper-status'] });
    },
    onError: (err: Error) => {
      setConfirm(null);
      setError(err.message);
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Manual sweep</CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-sm text-slate-600">
          Set the criteria (an account must match all of them), run a dry run to see who matches,
          then remove them now or start their grace period.
        </p>
        {error && <ErrorBox>{error}</ErrorBox>}
        <RuleFields rule={rule} onChange={setRule} tiers={tiers} showName={false} />
        <Button onClick={() => dryRun.mutate()} disabled={dryRun.isPending}>
          {dryRun.isPending ? 'Checking accounts…' : 'Dry run'}
        </Button>
        {preview && (
          <div className="space-y-3 border-t border-slate-200 pt-4">
            {stale && <NoteBox>The criteria changed since this dry run — run it again before acting.</NoteBox>}
            <PreviewResults
              result={preview.result}
              tiers={tiers}
              filename={`sweep-preview-${preview.result.run_id}.csv`}
              selection={selection}
            />
            <div className="flex flex-wrap gap-2">
              <Button
                variant="destructive"
                disabled={stale || running || pickedCandidates.length === 0 || execute.isPending}
                onClick={() => setConfirm('remove')}
              >
                <Trash2 size={14} /> Remove {pickedCandidates.length} now
              </Button>
              <Button
                variant="outline"
                disabled={stale || running || pickedMarkable.length === 0 || execute.isPending}
                onClick={() => setConfirm('mark')}
              >
                Mark {pickedMarkable.length} for removal in {grace} days
              </Button>
            </div>
            {running && <p className="text-xs text-slate-500">A sweep is running — wait for it to finish.</p>}
          </div>
        )}
      </CardBody>

      <Dialog
        open={confirm === 'remove'}
        onOpenChange={(o) => !execute.isPending && !o && setConfirm(null)}
        title={`Remove ${plural(pickedCandidates.length, 'account')} now?`}
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={execute.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => execute.mutate('remove')} disabled={execute.isPending}>
              {execute.isPending ? 'Starting…' : `Remove ${pickedCandidates.length}`}
            </Button>
          </>
        }
      >
        <div className="space-y-2 text-sm text-slate-700">
          <p>
            No grace period. For each account the container is stopped and <b>all its data is purged</b>{' '}
            (websites, chats, AI credentials). <b className="text-red-700">This can't be undone.</b>
          </p>
          <p>
            Each account is checked once more right before it's removed. Your external system gets a{' '}
            <code className="font-mono text-xs">tenant.removed</code> webhook for each (if a URL is set),
            and every removal goes into the removal log.
          </p>
        </div>
      </Dialog>

      <Dialog
        open={confirm === 'mark'}
        onOpenChange={(o) => !execute.isPending && !o && setConfirm(null)}
        title={`Mark ${plural(pickedMarkable.length, 'account')} for removal?`}
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={execute.isPending}>
              Cancel
            </Button>
            <Button onClick={() => execute.mutate('mark')} disabled={execute.isPending}>
              {execute.isPending ? 'Starting…' : `Mark ${pickedMarkable.length}`}
            </Button>
          </>
        }
      >
        <div className="space-y-2 text-sm text-slate-700">
          <p>
            Starts a <b>{grace}-day</b> grace period for each account. Your external system gets a{' '}
            <code className="font-mono text-xs">tenant.removal_marked</code> webhook for each.
          </p>
          <p>
            When the grace period ends, the automatic sweeper checks each account again against these
            criteria: still matching → removed (stop &amp; purge); used again → unmarked.
          </p>
          {!data.config.enabled && (
            <NoteBox>
              The automatic sweeper is off, so marked accounts are not removed until you turn it on (or
              remove them by hand).
            </NoteBox>
          )}
        </div>
      </Dialog>
    </Card>
  );
}

type AutoDraft = Pick<SweeperConfig, 'enabled' | 'interval_hours' | 'rules' | 'grace_days' | 'max_removals_per_run'>;

function autoDraft(c: SweeperConfig): AutoDraft {
  return {
    enabled: c.enabled,
    interval_hours: c.interval_hours,
    rules: c.rules,
    grace_days: c.grace_days,
    max_removals_per_run: c.max_removals_per_run,
  };
}

function AutoSweeperCard({ tiers, data, running }: { tiers: Tier[]; data: SweeperConfigResponse; running: boolean }) {
  const qc = useQueryClient();
  const saved = autoDraft(data.config);
  const [draft, setDraft] = useState<AutoDraft>(saved);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<SweepPreview | null>(null);
  const [confirmRun, setConfirmRun] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);

  // Reload after saving: the server normalizes (trims names etc.), and the
  // form should show exactly what was stored.
  const save = useMutation({
    mutationFn: async () => {
      await api.put('/api/sweeper/config', draft);
      return api.get<SweeperConfigResponse>('/api/sweeper/config');
    },
    onSuccess: (fresh) => {
      setError(null);
      qc.setQueryData(['sweeper-config'], fresh);
      setDraft(autoDraft(fresh.config));
      qc.invalidateQueries({ queryKey: ['sweeper-status'] });
    },
    onError: (err: Error) => setError(err.message),
  });
  const previewRun = useMutation({
    mutationFn: () => api.post<SweepPreview>('/api/sweeper/run', { dry_run: true, rules: draft.rules }),
    onSuccess: (r) => {
      setPreview(r);
      setError(null);
      qc.invalidateQueries({ queryKey: ['sweeper-runs'] });
    },
    onError: (err: Error) => setError(err.message),
  });
  const runNow = useMutation({
    mutationFn: () => api.post<{ run_id: string }>('/api/sweeper/run-now'),
    onSuccess: () => {
      setConfirmRun(false);
      setError(null);
      qc.invalidateQueries({ queryKey: ['sweeper-status'] });
    },
    onError: (err: Error) => {
      setConfirmRun(false);
      setError(err.message);
    },
  });

  const setRule = (i: number, r: SweepRule) =>
    setDraft({ ...draft, rules: draft.rules.map((x, j) => (j === i ? r : x)) });
  const addRule = () => {
    let n = draft.rules.length + 1;
    while (draft.rules.some((r) => r.name === `Rule ${n}`)) n++;
    setDraft({ ...draft, rules: [...draft.rules, newRule(`Rule ${n}`)] });
  };
  const removeRule = (i: number) => setDraft({ ...draft, rules: draft.rules.filter((_, j) => j !== i) });
  const toBeMarked = preview?.candidates.filter((c) => !c.marked_at).length ?? 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Automatic sweeper</CardTitle>
          <Badge tone={data.config.enabled ? 'success' : 'neutral'}>{data.config.enabled ? 'on' : 'off'}</Badge>
        </div>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-sm text-slate-600">
          Each run: (1) accounts matching any rule are marked — a grace period starts and your system
          gets the "marked" webhook; (2) accounts whose grace period is over are checked again: still
          matching → removed (stop &amp; purge), used again → unmarked.
        </p>
        {error && <ErrorBox>{error}</ErrorBox>}
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer select-none h-10">
            <input
              type="checkbox"
              className="cursor-pointer"
              checked={draft.enabled}
              onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
            />
            Run automatically
          </label>
          <div className="w-32">
            <Label>Every (hours)</Label>
            <Input
              type="number"
              min={1}
              value={draft.interval_hours}
              onChange={(e) => setDraft({ ...draft, interval_hours: toInt(e.target.value) })}
            />
          </div>
          <div className="w-32">
            <Label>Grace period (days)</Label>
            <Input
              type="number"
              min={1}
              value={draft.grace_days}
              onChange={(e) => setDraft({ ...draft, grace_days: toInt(e.target.value) })}
            />
          </div>
          <div className="w-44">
            <Label>Max removals per run</Label>
            <Input
              type="number"
              min={1}
              value={draft.max_removals_per_run}
              onChange={(e) => setDraft({ ...draft, max_removals_per_run: toInt(e.target.value) })}
            />
          </div>
        </div>
        <div className="text-xs text-slate-500">
          Last run: {data.state.last_run_at ? relativeTime(data.state.last_run_at) : 'never'} · Next run:{' '}
          {data.config.enabled && data.state.next_run_at
            ? Date.parse(data.state.next_run_at) <= Date.now()
              ? 'within a minute'
              : relativeTime(data.state.next_run_at)
            : 'off'}
        </div>

        <div className="space-y-3">
          <div className="text-xs font-medium text-slate-700">
            Rules <span className="font-normal text-slate-500">— an account is a candidate if it matches any rule</span>
          </div>
          {draft.rules.map((r, i) => (
            <div key={i} className="rounded-md border border-slate-200 p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="text-xs text-slate-500">{describeRule(r, tiers)}</div>
                {draft.rules.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => removeRule(i)} title="Remove this rule">
                    <X size={14} />
                  </Button>
                )}
              </div>
              <RuleFields rule={r} onChange={(nr) => setRule(i, nr)} tiers={tiers} showName />
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addRule} disabled={draft.rules.length >= 20}>
            <Plus size={12} /> Add rule
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-4">
          <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          {dirty && (
            <Button variant="ghost" onClick={() => setDraft(saved)}>
              Discard changes
            </Button>
          )}
          <Button variant="outline" onClick={() => previewRun.mutate()} disabled={previewRun.isPending}>
            {previewRun.isPending ? 'Checking accounts…' : 'Preview'}
          </Button>
          <Button
            variant="outline"
            onClick={() => setConfirmRun(true)}
            disabled={dirty || running || runNow.isPending}
            title={dirty ? 'Save your changes first' : undefined}
          >
            Run now
          </Button>
        </div>

        {preview && (
          <div className="space-y-2 border-t border-slate-200 pt-4">
            <div className="text-sm text-slate-700">
              Preview: <b>{plural(toBeMarked, 'account')}</b> would be marked on the next run
              {preview.candidates.length > toBeMarked && <> ({preview.candidates.length - toBeMarked} already marked)</>}.
            </div>
            <PreviewResults result={preview} tiers={tiers} filename={`sweeper-preview-${preview.run_id}.csv`} />
          </div>
        )}
      </CardBody>

      <Dialog
        open={confirmRun}
        onOpenChange={(o) => !runNow.isPending && setConfirmRun(o)}
        title="Run the automatic sweeper now?"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmRun(false)} disabled={runNow.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => runNow.mutate()} disabled={runNow.isPending}>
              {runNow.isPending ? 'Starting…' : 'Run now'}
            </Button>
          </>
        }
      >
        <div className="space-y-2 text-sm text-slate-700">
          <p>
            Runs one pass with the saved rules: marks new matching accounts ({data.config.grace_days}-day grace
            period, "marked" webhook) and removes accounts whose grace period is over (stop &amp; purge,
            "removed" webhook), up to {data.config.max_removals_per_run} removals.
          </p>
          {!data.config.enabled && <p>The schedule stays off — this is a single run.</p>}
        </div>
      </Dialog>
    </Card>
  );
}

type DeliveryResult = { status: 'delivered' | 'failed' | 'not_configured'; httpStatus?: number; error?: string };

function WebhooksCard({ data }: { data: SweeperConfigResponse }) {
  const qc = useQueryClient();
  const saved = { marked_webhook_url: data.config.marked_webhook_url, removed_webhook_url: data.config.removed_webhook_url };
  const [urls, setUrls] = useState(saved);
  const [secret, setSecret] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [tests, setTests] = useState<Partial<Record<'marked' | 'removed', DeliveryResult>>>({});
  const [docsOpen, setDocsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // The spec plus what this control plane is configured to send, so the copied
  // text is self-contained for the implementer.
  const docs = useMemo(() => {
    const cfg = ['', '## Your current Godmode configuration', ''];
    cfg.push(
      data.config.marked_webhook_url
        ? '- Marked / postponed / unmarked events go to: ' + data.config.marked_webhook_url
        : '- Marked / postponed / unmarked events: no URL set yet'
    );
    cfg.push(
      data.config.removed_webhook_url
        ? '- Removed events go to: ' + data.config.removed_webhook_url
        : '- Removed events: no URL set yet'
    );
    cfg.push(
      data.webhook_secret === 'ok'
        ? '- Signature: enabled, every request carries X-Godmode-Signature'
        : '- Signature: NOT enabled yet, no shared secret is set in Godmode'
    );
    return webhookReceiverDocs.trimEnd() + '\n' + cfg.join('\n') + '\n';
  }, [data]);

  const copyDocs = async () => {
    try {
      await navigator.clipboard.writeText(docs);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setError('Could not copy — select the text in the dialog and copy it manually.');
    }
  };
  const urlsDirty =
    urls.marked_webhook_url !== saved.marked_webhook_url || urls.removed_webhook_url !== saved.removed_webhook_url;

  const save = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      await api.put('/api/sweeper/config', body);
      return api.get<SweeperConfigResponse>('/api/sweeper/config');
    },
    onSuccess: (fresh) => {
      setSecret('');
      setError(null);
      qc.setQueryData(['sweeper-config'], fresh);
      setUrls({
        marked_webhook_url: fresh.config.marked_webhook_url,
        removed_webhook_url: fresh.config.removed_webhook_url,
      });
    },
    onError: (err: Error) => setError(err.message),
  });
  const test = useMutation({
    mutationFn: async (kind: 'marked' | 'removed') => ({
      kind,
      result: await api.post<DeliveryResult>('/api/sweeper/webhooks/test', { kind }),
    }),
    onSuccess: ({ kind, result }) => setTests((t) => ({ ...t, [kind]: result })),
    onError: (err: Error) => setError(err.message),
  });

  const generate = () => {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    setSecret(Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join(''));
  };

  const urlRow = (kind: 'marked' | 'removed', label: string, help: ReactNode) => {
    const key = kind === 'marked' ? 'marked_webhook_url' : 'removed_webhook_url';
    const result = tests[kind];
    const failure: WebhookFailure | null = data.last_webhook_failure[kind];
    return (
      <div className="space-y-1">
        <Label>{label}</Label>
        <div className="flex gap-2">
          <Input
            value={urls[key]}
            placeholder="https://your-system.example.com/hooks/godmode"
            onChange={(e) => setUrls({ ...urls, [key]: e.target.value })}
          />
          <Button
            variant="outline"
            onClick={() => test.mutate(kind)}
            disabled={!saved[key] || urls[key] !== saved[key] || test.isPending}
            title={urls[key] !== saved[key] ? 'Save first' : !saved[key] ? 'No URL saved' : 'POST an event:"test" payload'}
          >
            Send test
          </Button>
        </div>
        <p className="text-xs text-slate-500">{help}</p>
        {result && (
          <p className={`text-xs ${result.status === 'delivered' ? 'text-emerald-700' : 'text-red-600'}`}>
            Test: {result.status === 'delivered' ? `delivered (HTTP ${result.httpStatus})` : result.status === 'not_configured' ? 'no URL saved' : `failed — ${result.error ?? `HTTP ${result.httpStatus}`}`}
          </p>
        )}
        {failure && (
          <p className="text-xs text-red-600">
            Last failed delivery {relativeTime(failure.at)}: {failure.event ?? 'event'}
            {failure.tenant_id ? ` for ${failure.tenant_id}` : ''} — {failure.error ?? `HTTP ${failure.http_status}`}
          </p>
        )}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Webhooks</CardTitle>
          <Button variant="outline" size="sm" onClick={() => setDocsOpen(true)}>
            <FileText size={12} /> Receiver docs
          </Button>
        </div>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-sm text-slate-600">
          The control plane POSTs JSON to these URLs so your other system can follow account removals.
          Deliveries are retried 3 times (now, after 30 seconds, after 5 minutes) and never block a mark or a
          removal. Deletes made through the external API don't notify — that call came from your system.
        </p>
        {error && <ErrorBox>{error}</ErrorBox>}
        {urlRow(
          'marked',
          'Marked for removal URL',
          <>
            Gets <code className="font-mono">tenant.removal_marked</code>,{' '}
            <code className="font-mono">tenant.removal_unmarked</code> and{' '}
            <code className="font-mono">tenant.removal_postponed</code>.
          </>
        )}
        {urlRow(
          'removed',
          'Removed URL',
          <>
            Gets <code className="font-mono">tenant.removed</code> — from the sweeper and from deletes on the
            tenant page.
          </>
        )}
        <div className="space-y-1">
          <Label>Signing secret</Label>
          <div className="text-xs text-slate-600 mb-1">
            {data.webhook_secret === 'ok' ? (
              <Badge tone="success">set</Badge>
            ) : data.webhook_secret === 'unreadable' ? (
              <Badge tone="danger">unreadable — enter it again</Badge>
            ) : (
              <Badge tone="neutral">not set</Badge>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Input
              className="font-mono text-xs flex-1 min-w-[240px]"
              value={secret}
              placeholder={data.webhook_secret === 'ok' ? 'Type a new secret to replace it' : 'At least 16 characters'}
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => setSecret(e.target.value)}
            />
            <Button variant="outline" onClick={generate}>
              Generate
            </Button>
            {data.webhook_secret !== 'none' && (
              <Button
                variant="ghost"
                onClick={() => save.mutate({ webhook_secret: null })}
                disabled={save.isPending}
              >
                Clear
              </Button>
            )}
          </div>
          <p className="text-xs text-slate-500">
            Copy it into your other system before saving — it's never shown again. Each request carries{' '}
            <code className="font-mono">X-Godmode-Signature: sha256=&lt;HMAC-SHA256 of the raw body&gt;</code>.
          </p>
        </div>
        <Button
          onClick={() => save.mutate({ ...urls, ...(secret ? { webhook_secret: secret } : {}) })}
          disabled={(!urlsDirty && !secret) || save.isPending}
        >
          {save.isPending ? 'Saving…' : 'Save webhooks'}
        </Button>
      </CardBody>

      <Dialog
        open={docsOpen}
        onOpenChange={setDocsOpen}
        title="Webhook receiver spec"
        size="xl"
        footer={
          <>
            <Button variant="outline" onClick={copyDocs}>
              {copied ? (
                <>
                  <Check size={14} /> Copied
                </>
              ) : (
                <>
                  <ClipboardCopy size={14} /> Copy markdown
                </>
              )}
            </Button>
            <Button onClick={() => setDocsOpen(false)}>Close</Button>
          </>
        }
      >
        <p className="text-xs text-slate-500 mb-3">
          Hand this to whoever builds the receiving endpoint: authentication, the payload fields and
          the response we expect. It ends with the URLs and signing state configured here.
        </p>
        <pre className="text-[11px] leading-relaxed font-mono whitespace-pre-wrap break-words text-slate-800 bg-slate-50 border border-slate-200 rounded-md p-3">
          {docs}
        </pre>
      </Dialog>
    </Card>
  );
}

function MarkedCard({ tiers, graceDays }: { tiers: Tier[]; graceDays: number }) {
  const qc = useQueryClient();
  const marked = useQuery({
    queryKey: ['sweeper-marked'],
    queryFn: () => api.get<{ tenants: MarkedTenant[] }>('/api/sweeper/marked'),
    refetchInterval: 30_000,
  });
  const [postponeFor, setPostponeFor] = useState<MarkedTenant | null>(null);
  const [days, setDays] = useState(graceDays);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    for (const key of ['sweeper-marked', 'sweeper-log', 'tenants']) qc.invalidateQueries({ queryKey: [key] });
  };
  const postpone = useMutation({
    mutationFn: ({ id, days }: { id: string; days: number }) =>
      api.post(`/api/tenants/${encodeURIComponent(id)}/removal/postpone`, { days }),
    onSuccess: () => {
      setPostponeFor(null);
      setError(null);
      refresh();
    },
    onError: (err: Error) => setError(err.message),
  });
  const unmark = useMutation({
    mutationFn: (id: string) => api.post(`/api/tenants/${encodeURIComponent(id)}/removal/unmark`),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (err: Error) => setError(err.message),
  });

  const list = marked.data?.tenants ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Marked for removal{list.length ? ` (${list.length})` : ''}</CardTitle>
      </CardHeader>
      <CardBody className="p-0">
        {error && (
          <div className="px-5 pt-4">
            <ErrorBox>{error}</ErrorBox>
          </div>
        )}
        {marked.isLoading ? (
          <div className="p-5 text-sm text-slate-500">Loading…</div>
        ) : !list.length ? (
          <div className="p-5 text-sm text-slate-500">No account is marked for removal.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-sm">
              <thead>
                <tr className={TABLE_HEAD}>
                  <th className="px-5 py-3">Tenant</th>
                  <th className="px-3 py-3">Customer</th>
                  <th className="px-3 py-3">Tier</th>
                  <th className="px-3 py-3">Removal</th>
                  <th className="px-3 py-3">Rule</th>
                  <th className="px-3 py-3">Last used</th>
                  <th className="px-3 py-3">Sites</th>
                  <th className="px-3 py-3">AI auth</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((t) => (
                  <tr key={t.id} className={ROW}>
                    <td className="px-5 py-3 font-mono text-xs">
                      <Link to={`/tenants/${t.id}`} className="text-slate-900 hover:underline">
                        {t.id}
                      </Link>
                    </td>
                    <td className="px-3 py-3 text-xs">
                      <div className="text-slate-800">{t.customer_email ?? '—'}</div>
                      {t.company_name && <div className="text-slate-500">{t.company_name}</div>}
                    </td>
                    <td className="px-3 py-3 text-xs">{tierLabel(tiers, t.tier)}</td>
                    <td className="px-3 py-3 text-xs whitespace-nowrap">
                      <span title={`Due ${formatDate(t.removal_due_at)} · marked ${formatDate(t.removal_marked_at)}`}>
                        <Badge tone="danger">{removalDueLabel(t.removal_due_at)}</Badge>
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs">
                      {t.removal_context?.rule === 'manual' ? 'marked by hand' : t.removal_context?.rule ?? '—'}
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap" title={formatDate(t.last_used_at)}>
                      {relativeTime(t.last_used_at)}
                    </td>
                    <td className="px-3 py-3 text-xs">{t.site_count ?? '?'}</td>
                    <td className="px-3 py-3 text-xs">{t.ai_authenticated ? 'yes' : 'no'}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setDays(graceDays);
                          setPostponeFor(t);
                        }}
                      >
                        Postpone…
                      </Button>{' '}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => unmark.mutate(t.id)}
                        disabled={unmark.isPending}
                        title="Clear the mark. A still-unused account is marked again on the next automatic run — postpone to leave it alone for a while."
                      >
                        Unmark
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>

      <PostponeDialog
        tenantId={postponeFor?.id ?? null}
        dueAt={postponeFor?.removal_due_at ?? null}
        days={days}
        onDays={setDays}
        pending={postpone.isPending}
        onCancel={() => setPostponeFor(null)}
        onConfirm={() => postponeFor && postpone.mutate({ id: postponeFor.id, days })}
      />
    </Card>
  );
}

function RecentSweepsCard({ tiers, onViewLog }: { tiers: Tier[]; onViewLog: (runId: string) => void }) {
  const runs = useQuery({
    queryKey: ['sweeper-runs'],
    queryFn: () => api.get<{ runs: SweepRunRow[] }>('/api/sweeper/runs?limit=20'),
  });
  const [open, setOpen] = useState<string | null>(null);
  const list = runs.data?.runs ?? [];

  const typeOf = (p: SweepRunRow['parameters']) =>
    p?.dry_run ? 'Dry run' : p?.action === 'remove' ? 'Remove' : p?.action === 'mark' ? 'Mark' : 'Automatic';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent sweeps</CardTitle>
      </CardHeader>
      <CardBody className="p-0">
        {runs.isLoading ? (
          <div className="p-5 text-sm text-slate-500">Loading…</div>
        ) : !list.length ? (
          <div className="p-5 text-sm text-slate-500">No sweeps yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead>
                <tr className={TABLE_HEAD}>
                  <th className="px-5 py-3">When</th>
                  <th className="px-3 py-3">Type</th>
                  <th className="px-3 py-3">Trigger</th>
                  <th className="px-3 py-3">Result</th>
                  <th className="px-3 py-3">By</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => {
                  const p = r.parameters;
                  return (
                    <tr key={r.id} className={ROW}>
                      <td className="px-5 py-3 text-xs text-slate-600 whitespace-nowrap" title={formatDate(r.created_at)}>
                        {relativeTime(r.created_at)}
                        <div className="font-mono text-[11px] text-slate-400">{r.run_id}</div>
                      </td>
                      <td className="px-3 py-3 text-xs">{typeOf(p)}</td>
                      <td className="px-3 py-3 text-xs">{p?.trigger ? RUN_TRIGGER[p.trigger] : '—'}</td>
                      <td className="px-3 py-3 text-xs text-slate-700">
                        {r.outcome === 'error' && <Badge tone="danger" className="mr-1.5">error</Badge>}
                        {p ? runSummaryText(p, !!p.dry_run) : '—'}
                        {r.error_message && <div className="text-red-600 mt-0.5">{r.error_message}</div>}
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-500">{r.operator_email ?? 'system'}</td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        {p?.dry_run ? (
                          <Button variant="ghost" size="sm" onClick={() => setOpen(open === r.run_id ? null : r.run_id)}>
                            {open === r.run_id ? 'Hide' : 'Preview'}
                          </Button>
                        ) : (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => onViewLog(r.run_id)}>
                              View log
                            </Button>{' '}
                            <a
                              className={LINK_BUTTON}
                              href={`/api/sweeper/log.csv?kind=all&run_id=${encodeURIComponent(r.run_id)}`}
                            >
                              <Download size={12} /> CSV
                            </a>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {open && <StoredPreview runId={open} tiers={tiers} />}
      </CardBody>
    </Card>
  );
}

// What a past dry run showed — the candidate list kept in its activity row.
function StoredPreview({ runId, tiers }: { runId: string; tiers: Tier[] }) {
  const run = useQuery({
    queryKey: ['sweeper-run', runId],
    queryFn: () => api.get<{ run: SweepRunRow }>(`/api/sweeper/runs/${encodeURIComponent(runId)}`),
  });
  const p = run.data?.run.parameters;
  const list = p?.candidate_list ?? [];

  const download = () => {
    const header = ['tenant_id', 'customer_email', 'company_name', 'tier', 'status', 'last_used_at', 'site_count', 'ai_authenticated', 'rule', 'checked'];
    downloadCsv(
      `sweep-preview-${runId}.csv`,
      toCsv(header, list.map((c) => [c.tenant_id, c.customer_email, c.company_name, c.tier, c.status, c.last_used_at, c.site_count, c.ai_authenticated, c.rule, c.checked]))
    );
  };

  return (
    <div className="border-t border-slate-200 px-5 py-4 space-y-3 bg-slate-50/60">
      {run.isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs text-slate-600">
              Dry run <span className="font-mono">{runId}</span>
              {p?.rules?.length ? <> · {p.rules.map((r) => `${r.name}: ${describeRule(r, tiers)}`).join(' | ')}</> : null}
              {p?.candidate_list_truncated && <> · list truncated to the first {list.length}</>}
            </div>
            <Button variant="outline" size="sm" onClick={download} disabled={!list.length}>
              <Download size={12} /> Download CSV
            </Button>
          </div>
          {!list.length ? (
            <div className="text-sm text-slate-500">No account matched.</div>
          ) : (
            <div className="overflow-x-auto border border-slate-200 rounded-md bg-white max-h-[360px] overflow-y-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className={TABLE_HEAD}>
                    <th className="px-3 py-2">Tenant</th>
                    <th className="px-3 py-2">Customer</th>
                    <th className="px-3 py-2">Tier</th>
                    <th className="px-3 py-2">Last used</th>
                    <th className="px-3 py-2">Sites</th>
                    <th className="px-3 py-2">AI auth</th>
                    <th className="px-3 py-2">Rule</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((c) => (
                    <tr key={c.tenant_id} className={ROW}>
                      <td className="px-3 py-2 font-mono text-xs">{c.tenant_id}</td>
                      <td className="px-3 py-2 text-xs">
                        {c.customer_email ?? '—'}
                        {c.company_name && <span className="text-slate-500"> · {c.company_name}</span>}
                      </td>
                      <td className="px-3 py-2 text-xs">{tierLabel(tiers, c.tier)}</td>
                      <td className="px-3 py-2 text-xs text-slate-500" title={formatDate(c.last_used_at)}>
                        {relativeTime(c.last_used_at)}
                      </td>
                      <td className="px-3 py-2 text-xs">{c.site_count ?? '?'}</td>
                      <td className="px-3 py-2 text-xs">{c.ai_authenticated ? 'yes' : 'no'}</td>
                      <td className="px-3 py-2 text-xs">{c.rule ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const LOG_PAGE = 50;

const MODE_LABEL: Record<string, string> = {
  stop_purge: 'stop & purge',
  stop: 'stop, data kept',
  cp_only: 'CP record only',
};

function eventDetail(e: RemovalLogEntry): string {
  const d = e.detail ?? {};
  if (e.kind === 'unmarked' && typeof d.reason === 'string') return UNMARK_REASON[d.reason] ?? d.reason;
  if (e.kind === 'postponed' && typeof d.days === 'number') return `+${d.days} days`;
  if (e.kind === 'marked' && e.due_at) return `due ${formatDate(e.due_at)}`;
  if (e.kind === 'removed' && e.mode) return MODE_LABEL[e.mode] ?? e.mode;
  return '';
}

function RemovalLogCard({
  tiers,
  filters,
  onFilters,
}: {
  tiers: Tier[];
  filters: LogFilters;
  onFilters: (f: LogFilters) => void;
}) {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState(filters.tenant);
  const qs = logQuery(filters);

  // New filters → first page. Search applies after a short pause in typing.
  useEffect(() => setPage(0), [qs]);
  useEffect(() => setSearch(filters.tenant), [filters.tenant]);
  useEffect(() => {
    if (search === filters.tenant) return;
    const t = setTimeout(() => onFilters({ ...filters, tenant: search.trim() }), 400);
    return () => clearTimeout(t);
  }, [search, filters, onFilters]);

  const log = useQuery({
    queryKey: ['sweeper-log', qs, page],
    queryFn: () =>
      api.get<{ entries: RemovalLogEntry[]; total: number }>(
        `/api/sweeper/log?${qs}${qs ? '&' : ''}limit=${LOG_PAGE}&offset=${page * LOG_PAGE}`
      ),
    refetchInterval: 30_000,
  });
  const set = (patch: Partial<LogFilters>) => onFilters({ ...filters, ...patch });
  const entries = log.data?.entries ?? [];
  const total = log.data?.total ?? 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Removal log</CardTitle>
          <a className={LINK_BUTTON} href={`/api/sweeper/log.csv${qs ? `?${qs}` : ''}`}>
            <Download size={12} /> Download CSV
          </a>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        <div className="px-5 py-4 flex flex-wrap items-end gap-3 border-b border-slate-200">
          <div className="w-40">
            <Label>Event</Label>
            <Select value={filters.kind} onChange={(e) => set({ kind: e.target.value as LogFilters['kind'] })}>
              <option value="removed">Removed</option>
              <option value="marked">Marked</option>
              <option value="unmarked">Unmarked</option>
              <option value="postponed">Postponed</option>
              <option value="all">All events</option>
            </Select>
          </div>
          <div className="w-44">
            <Label>Trigger</Label>
            <Select value={filters.trigger} onChange={(e) => set({ trigger: e.target.value as LogFilters['trigger'] })}>
              <option value="">All</option>
              {(Object.keys(TRIGGER) as RemovalTrigger[]).map((k) => (
                <option key={k} value={k}>
                  {TRIGGER[k]}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-40">
            <Label>From (UTC)</Label>
            <Input type="date" value={filters.from} onChange={(e) => set({ from: e.target.value })} />
          </div>
          <div className="w-40">
            <Label>To (UTC)</Label>
            <Input type="date" value={filters.to} onChange={(e) => set({ to: e.target.value })} />
          </div>
          <div className="w-56">
            <Label>Tenant, email or company</Label>
            <Input value={search} placeholder="Search…" onChange={(e) => setSearch(e.target.value)} />
          </div>
          {filters.run_id && (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700 h-8">
              run <span className="font-mono">{filters.run_id}</span>
              <button type="button" onClick={() => set({ run_id: '' })} aria-label="Clear run filter" className="text-slate-400 hover:text-slate-700">
                <X size={12} />
              </button>
            </span>
          )}
        </div>

        {log.isError ? (
          <div className="p-5">
            <ErrorBox>{(log.error as Error).message}</ErrorBox>
          </div>
        ) : log.isLoading ? (
          <div className="p-5 text-sm text-slate-500">Loading…</div>
        ) : !entries.length ? (
          <div className="p-5 text-sm text-slate-500">Nothing in the log for these filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px] text-sm">
              <thead>
                <tr className={TABLE_HEAD}>
                  <th className="px-5 py-3">When</th>
                  <th className="px-3 py-3">Event</th>
                  <th className="px-3 py-3">Tenant</th>
                  <th className="px-3 py-3">Customer</th>
                  <th className="px-3 py-3">Tier</th>
                  <th className="px-3 py-3">Registered</th>
                  <th className="px-3 py-3">Last used</th>
                  <th className="px-3 py-3">Sites</th>
                  <th className="px-3 py-3">AI auth</th>
                  <th className="px-3 py-3">Rule</th>
                  <th className="px-3 py-3">Trigger</th>
                  <th className="px-3 py-3">Operator</th>
                  <th className="px-3 py-3">Outcome</th>
                  <th className="px-5 py-3">Webhook</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className={ROW}>
                    <td className="px-5 py-3 text-xs text-slate-600 whitespace-nowrap" title={formatDate(e.created_at)}>
                      {relativeTime(e.created_at)}
                    </td>
                    <td className="px-3 py-3 text-xs">
                      <Badge tone={KIND_TONE[e.kind]}>{e.kind}</Badge>
                      {eventDetail(e) && <div className="text-slate-500 mt-0.5">{eventDetail(e)}</div>}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">
                      {e.kind === 'removed' && e.outcome === 'success' ? (
                        e.tenant_id
                      ) : (
                        <Link to={`/tenants/${e.tenant_id}`} className="text-slate-900 hover:underline">
                          {e.tenant_id}
                        </Link>
                      )}
                    </td>
                    <td className="px-3 py-3 text-xs">
                      <div className="text-slate-800">{e.customer_email ?? '—'}</div>
                      {e.company_name && <div className="text-slate-500">{e.company_name}</div>}
                    </td>
                    <td className="px-3 py-3 text-xs">{tierLabel(tiers, e.tier)}</td>
                    <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap" title={formatDate(e.tenant_created_at)}>
                      {e.tenant_created_at ? new Date(e.tenant_created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap" title={formatDate(e.last_used_at)}>
                      {e.last_used_at ? new Date(e.last_used_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-3 py-3 text-xs">{e.site_count ?? '?'}</td>
                    <td className="px-3 py-3 text-xs">
                      {e.ai_authenticated === null ? '?' : e.ai_authenticated ? 'yes' : 'no'}
                    </td>
                    <td className="px-3 py-3 text-xs">{e.rule === 'manual' ? 'by hand' : e.rule ?? '—'}</td>
                    <td className="px-3 py-3 text-xs whitespace-nowrap">{TRIGGER[e.trigger] ?? e.trigger}</td>
                    <td className="px-3 py-3 text-xs text-slate-500">{e.operator_email ?? '—'}</td>
                    <td className="px-3 py-3 text-xs">
                      <Badge tone={statusTone(e.outcome)}>{e.outcome}</Badge>
                      {e.error_message && (
                        <div className="text-red-600 mt-0.5 max-w-[220px] break-words">{e.error_message}</div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-xs">
                      {e.webhook_status ? (
                        <Badge tone={WEBHOOK_TONE[e.webhook_status] ?? 'neutral'}>{e.webhook_status.replace('_', ' ')}</Badge>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > LOG_PAGE && (
          <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-600">
            <span>
              {page * LOG_PAGE + 1}–{Math.min(total, (page + 1) * LOG_PAGE)} of {total}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={(page + 1) * LOG_PAGE >= total}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
